import { useEffect, useState, useMemo, useRef } from "react";
import { useEditMode } from "../../context/EditModeContext";

export function useFormEdit<T extends Record<string, any>>(initialData?: T) {
  const [formData, setFormData] = useState<T>(() => initialData ?? ({} as T));
  const [originalData, setOriginalData] = useState<T>(() => initialData ?? ({} as T));
  const { isEditing } = useEditMode();
  
  const hasInitialized = useRef(false);

  // Sync when API data loads (only once)
  useEffect(() => {
    if (!initialData || hasInitialized.current) return;
    
    setFormData(initialData);
    setOriginalData(initialData);
    hasInitialized.current = true;
  }, [initialData]);

  // Calculate hasUnsavedChanges with useMemo instead of useEffect
  const hasUnsavedChanges = useMemo(() => {
    const keys = new Set([
      ...Object.keys(originalData),
      ...Object.keys(formData),
    ]);

    return Array.from(keys).some((key) => {
      const newVal = formData[key];
      const oldVal = originalData[key];

      // Treat empty values as equal
      if (
        (newVal === "" || newVal === null || newVal === undefined) &&
        (oldVal === "" || oldVal === null || oldVal === undefined)
      ) {
        return false;
      }

      return newVal !== oldVal;
    });
  }, [formData, originalData]);

  return {
    formData,
    setFormData,
    hasUnsavedChanges,
    isEditing,
  };
}